import type { Brief, FileItem, ImplementedRubricSpec, People, Task, QualityChanges, RubricCriterion, RubricLayout } from '@/lib/types'
export interface Round {
  scientific_scope?: string
  closed_reason?: string
  round_id: string
  task_id: string
  kind: 'submission' | 'quality' | 'scientific'
  status: string
  version: number
  created_at: string
  title: string
  programs?: string[]
  program: string
  author_email: string
  author_id?: string
  peer_id?: string
  peer_email?: string
  handoff_error?: string
  handoff_attempts?: number
  my_review_completed?: boolean
  agent_job_id?: string
  return_to_stage?: number
  /** The author's reply to the feedback that returned the previous round, written when resubmitting. */
  author_reply?: string
}
/** A file of the earlier round that this round no longer has. */
export interface RemovedFile { file_id: string; filename: string; kind: string; size: number }
/** Execution files since an earlier round: new file ids, re-uploads with their earlier size, removed files. */
export interface FileChanges { added: string[]; replaced: { file_id: string; size: number }[]; removed: RemovedFile[] }
export const filesChanged = (changes?: FileChanges | null) =>
  !!changes && changes.added.length + changes.replaced.length + changes.removed.length > 0
/** The peer review round before this one, frozen as it was submitted, and the feedback that returned it. */
export interface PreviousSubmission {
  round_id: string
  created_at: string
  status?: string
  prompt: string
  brief?: Brief | null
  rubric: RubricCriterion[]
  rubric_layout?: RubricLayout
  /** Execution files added, re-uploaded or removed since that round. */
  files?: FileChanges
  feedback?: string | null
  feedback_by_id?: string | null
  feedback_by_email?: string | null
  feedback_at?: string | null
}
export interface ReviewEntry {
  entry_id: string
  at: string
  actor: { user_id: string; email: string; role: string }
  verdict?: 'ready' | 'needs_changes'
  feedback?: string
  text?: string
  reference?: Record<string, string>
  to_stage?: number
  decision?: string
}
export interface ReviewDetail {
  quality_changes?: QualityChanges | null
  publication_blockers?: string[]
  agent_review?: {
    status: string
    attempts: number
    activity?: string
    error?: string
    report?: { verdict: 'ready' | 'needs_changes'; feedback: string }
  }
  /** The authoring agent's open question (notify_expert) and the task expert's reply; peer approval sends the reply. */
  agent_question?: { message: string; requested_at?: string; response?: string | null; returned_by?: string | null; returned_by_id?: string | null; returned_at?: string | null } | null
  round: Round
  previous_submission?: PreviousSubmission | null
  /** Earlier peer review rounds this one can be compared with, newest first. */
  comparable_rounds?: Pick<Round, 'round_id' | 'created_at' | 'status'>[]
  people?: People
  submission: { task: Task; files: FileItem[]; handoff?: { warnings?: string[] }; implemented_rubric?: ImplementedRubricSpec }
  recommendations: ReviewEntry[]
  comments: ReviewEntry[]
  decisions?: ReviewEntry[]
  is_current: boolean
  can_recommend: boolean
  can_decide: boolean
  task_revision: number
  publication?: { status: string }
}

export interface CoachReview {
  review_id: string
  created_at: string
  status: string
}

export interface ReviewHistory {
  current_round_id?: string | null
  rounds: Round[]
  coach_reviews: CoachReview[]
}
